import { useState } from "react"

import { Switch } from "@headlessui/react"
import { twMerge } from "tailwind-merge"

import { useFieldContext } from "./FieldProvider"
import { PathBuilder } from "~/src/lib/iname"
import { dataFlag } from "~/src/lib/jsx"

type ToggleProps_ = {
  id?: string
  name?: string | PathBuilder
  className?: string
  onChange?: (value: boolean) => void
  disabled?: boolean
  errored?: boolean
}

export type ToggleProps =
  | ({
      defaultChecked?: boolean // Uncontrolled mode
      checked?: never
    } & ToggleProps_)
  | ({
      checked?: boolean // Controlled mode
      defaultChecked?: never
    } & ToggleProps_)

/**
 * A BeeKit toggle switch implemented with Headless UI's Switch. The component can be used in controlled or uncontrolled
 * mode. Set `checked` prop to control the state of the component. Only `checked` or `defaultChecked` can be set at a
 * time.
 * @param props
 * @param props.checked control the state of the component.
 * @param props.defaultChecked set the initial state of the component allowing it to be uncontrolled.
 */
export function Toggle(props: ToggleProps) {
  const { name, id, defaultChecked, disabled, className, onChange, errored: errored_ } = props
  const [checked_, setChecked] = useState(defaultChecked ?? false)

  const isControlled = "checked" in props
  const checked = isControlled ? props.checked : checked_
  const fieldContext = useFieldContext()
  const errored = errored_ ?? fieldContext?.hasErrors

  const handleChange = (value: boolean) => {
    fieldContext?.descendentChange?.()
    if (!isControlled) setChecked(value)
    onChange?.(value)
  }

  return (
    <>
      {/* An hidden input is added to implement a behaviour that mimics how Rails' checkbox helpers behave. If we rely
      `Switch`'s hidden input, it will treat it as a checkbox meaning that the value will be set to "on", and the input
      will only be present when it is checked. */}
      {name != null ? (
        <input id={id} disabled={disabled} name={name.toString()} type="hidden" value={checked ? "1" : "0"} />
      ) : (
        <></>
      )}
      <Switch
        disabled={disabled}
        checked={checked}
        onChange={handleChange}
        className={twMerge(
          "group relative inline-flex h-5 w-10 cursor-pointer items-center rounded-full bg-gray-300 transition",
          "disabled:cursor-not-allowed disabled:bg-gray-200",
          "focus:ring-navy-800 focus:ring-2 focus:ring-offset-2 focus:outline-none",
          "data-checked:bg-navy-800 data-errored:bg-red-600 data-errored:focus:ring-red-600",
          className
        )}
        {...dataFlag(errored, "errored")}
        {...dataFlag(checked, "checked")}
      >
        <span
          className={twMerge(
            "inline-block h-4 w-4 translate-x-0.5 transform rounded-full bg-white transition-transform group-disabled:bg-gray-300",
            checked && "translate-x-5.5"
          )}
        />
      </Switch>
    </>
  )
}
